import { FACILITY_REPAIR_COST_PER_POINT as REPAIR_COST_PER_POINT, HULL_ZONE_IDS, HULL_ZONE_LABELS } from '../../shared/constants.js';
import type { FacilityActionPayload, HullZoneId } from '../../shared/types.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import { state as controllerState } from '../controllerState.js';
import { htmlEl } from './stationDisplays.js';
import { createFacilityHead } from './facilityUi.js';

type Options = {
  controls: HTMLDivElement | null;
  getControllerNetwork: () => ControllerNetwork | null;
};

const DAMAGED_BELOW = 80;
const CRITICAL_BELOW = 50;

const zoneCost = (integrity: number): number => Math.ceil(100 - integrity) * REPAIR_COST_PER_POINT;

type ZoneRefs = { card: HTMLElement; bar: HTMLElement; value: HTMLElement; cost: HTMLElement; btn: HTMLButtonElement };

export const createFacilityRepairController = ({ controls, getControllerNetwork }: Options) => {
  const zones = new Map<HullZoneId, ZoneRefs>();
  let repairAllBtn: HTMLButtonElement | null = null;
  let repairAllCost: HTMLElement | null = null;
  let setCredits: ((value: number) => void) | null = null;

  const emit = (payload: FacilityActionPayload): void => {
    getControllerNetwork()?.facilityAction(payload);
  };

  const render = (): void => {
    if (!controls) return;
    controls.innerHTML = '';
    controls.classList.add('facility-role-layout');
    zones.clear();

    const head = createFacilityHead('HULL REPAIR');
    setCredits = head.setCredits;

    const hint = htmlEl('p', 'fc-hint', `Dockyard repairs restore integrity at ${REPAIR_COST_PER_POINT} CR per point.`);

    const grid = htmlEl('div', 'fc-zones');
    for (const zoneId of HULL_ZONE_IDS) {
      const card = htmlEl('div', 'facility-item-row fc-zone');
      const top = htmlEl('div', 'fc-zone-top');
      top.append(htmlEl('span', 'fc-card-name', HULL_ZONE_LABELS[zoneId]));
      const value = htmlEl('span', 'fc-zone-value');
      top.append(value);
      const track = htmlEl('div', 'fc-zone-track');
      const bar = htmlEl('div', 'fc-zone-bar');
      track.append(bar);
      const foot = htmlEl('div', 'fc-card-foot');
      const cost = htmlEl('div', 'facility-price fc-price');
      const btn = htmlEl('button', 'facility-buy-btn', 'Repair');
      btn.type = 'button';
      btn.addEventListener('click', () => {
        const code = controllerState.currentRoomCode;
        if (code) emit({ code, type: 'repair_hull', zoneId });
      });
      foot.append(cost, btn);
      card.append(top, track, foot);
      grid.append(card);
      zones.set(zoneId, { card, bar, value, cost, btn });
    }

    const all = htmlEl('div', 'fc-repair-all');
    repairAllCost = htmlEl('div', 'facility-price fc-price');
    repairAllBtn = htmlEl('button', 'facility-buy-btn fc-repair-all-btn', 'Repair all zones');
    repairAllBtn.type = 'button';
    repairAllBtn.addEventListener('click', () => {
      const code = controllerState.currentRoomCode;
      if (code) emit({ code, type: 'repair_hull', zoneId: 'all' });
    });
    all.append(htmlEl('span', 'fc-card-name', 'Whole hull'), repairAllCost, repairAllBtn);

    const body = htmlEl('div', 'fc-list');
    body.append(hint, grid, all);
    controls.append(head.root, body);
    updatePanel();
  };

  const updatePanel = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!roomState) return;
    const credits = roomState.credits;
    setCredits?.(credits);

    const hullZones = controllerState.lastSnapshot?.submarine?.hullZones;
    if (!hullZones) return;

    let total = 0;
    for (const zoneId of HULL_ZONE_IDS) {
      const refs = zones.get(zoneId);
      if (!refs) continue;
      const integrity = hullZones[zoneId] ?? 100;
      const cost = zoneCost(integrity);
      total += cost;
      const intact = integrity >= 100;
      refs.bar.style.width = `${integrity}%`;
      refs.bar.dataset.level = integrity < CRITICAL_BELOW ? 'critical' : integrity < DAMAGED_BELOW ? 'damaged' : 'ok';
      refs.value.textContent = `${Math.round(integrity)}%`;
      refs.cost.textContent = intact ? '✓ Intact' : `${cost.toLocaleString()} CR`;
      refs.cost.classList.toggle('unaffordable', !intact && credits < cost);
      refs.btn.disabled = intact || credits < cost;
    }

    if (repairAllBtn && repairAllCost) {
      repairAllCost.textContent = total === 0 ? 'All intact' : `${total.toLocaleString()} CR`;
      repairAllCost.classList.toggle('unaffordable', total > 0 && credits < total);
      repairAllBtn.disabled = total === 0 || credits < total;
    }
  };

  return { render, updatePanel };
};
